import { ANALYTICS_BATCH_MAX, ANALYTICS_WIRE_VERSION, toWire } from "@highmarch/app";
import type { Analytics, AnalyticsEvent, WireBatch, WireEvent } from "@highmarch/app";

/** What the sink needs from the browser, so tests can run it without one. */
export interface BeaconEnv {
  /** Queues a POST that survives the page closing (navigator.sendBeacon); false when the browser refused it. */
  send(url: string, body: string): boolean;
  setInterval(fn: () => void, ms: number): unknown;
  /** Runs `fn` when the page is hidden or closed (visibilitychange → hidden, pagehide). */
  onHide(fn: () => void): void;
}

/** A batch goes out at this many events, every `FlushEveryMs`, and whenever the page is hidden. */
export const FlushAtEvents = 10;
export const FlushEveryMs = 30_000;
/** Events kept while sends keep failing (offline): the oldest are dropped past this. */
export const MaxQueued = 100;

/**
 * The web's `Analytics` (ADR 0015): batches events and posts them to the site's own `/api/e` with sendBeacon, which
 * never delays the game and survives the tab closing. Offline, the queue waits (bounded) for the next flush.
 */
export class BeaconAnalytics implements Analytics {
  private queue: WireEvent[] = [];

  constructor(private readonly installId: string, private readonly channel: string, private readonly build: string, private readonly env: BeaconEnv, private readonly url = "./api/e") {
    env.setInterval(() => this.flush(), FlushEveryMs);
    env.onHide(() => this.flush());
  }

  track(event: AnalyticsEvent): void {
    this.queue.push(toWire(event));
    if (this.queue.length > MaxQueued) this.queue.splice(0, this.queue.length - MaxQueued);
    if (this.queue.length >= FlushAtEvents) this.flush();
  }

  /** Sends what is queued, a batch at a time; what the browser refuses stays queued for the next flush. */
  flush(): void {
    while (this.queue.length > 0) {
      const events = this.queue.slice(0, ANALYTICS_BATCH_MAX);
      const batch: WireBatch = { v: ANALYTICS_WIRE_VERSION, id: this.installId, ch: this.channel, b: this.build, e: events };
      let sent = false;
      try { sent = this.env.send(this.url, JSON.stringify(batch)); } catch { sent = false; }
      if (!sent) return;
      this.queue.splice(0, events.length);
    }
  }
}

/** The real browser behind `BeaconEnv`. */
export const browserBeaconEnv: BeaconEnv = {
  send: (url, body) => typeof navigator.sendBeacon === "function" && navigator.sendBeacon(url, new Blob([body], { type: "application/json" })),
  setInterval: (fn, ms) => setInterval(fn, ms),
  onHide: (fn) => {
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") fn(); });
    addEventListener("pagehide", fn);
  },
};
